<template>
  <ModalBase
    class="silent-operator-warning-modal"
    :show="show"
    width="660"
    persistent
    hide-close
    content-class="silent-operator-warning-dialog"
    :title="lang.title"
  >
    <div slot="content" class="silent-operator-warning">
      <div class="silent-operator-warning__icon" aria-hidden="true">!</div>
      <div class="silent-operator-warning__message">{{ lang.message }}</div>
      <div class="silent-operator-warning__penalty">{{ lang.penalty }}</div>
      <div class="silent-operator-warning__question">{{ lang.question }}</div>
      <div v-if="timerReady" class="silent-operator-warning__countdown">
        {{ lang.timeLeft }}
      </div>
      <div v-else class="silent-operator-warning__countdown">
        {{ lang.preparing }}
      </div>
    </div>
    <div slot="actions" class="silent-operator-warning__actions">
      <Button
        class="silent-operator-warning__button"
        :text="lang.decline"
        color="error"
        outline
        :disabled="disabled || !timerReady"
        :handleClick="decline"
      />
      <Button
        class="silent-operator-warning__button"
        :text="lang.accept"
        color="primary"
        :disabled="disabled || !timerReady"
        :handleClick="accept"
      />
    </div>
  </ModalBase>
</template>

<script>
import ModalBase from "./ModalBase";
import Button from "./Button";

export default {
  name: "ModalSilentOperatorWarning",
  components: { ModalBase, Button },
  props: {
    show: { type: Boolean, default: false },
    warning: { type: Object, required: true },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      secondsLeft: 0,
      timerReady: false,
      timer: null,
      timeoutEmitted: false,
    };
  },
  computed: {
    lang() {
      return {
        title: this.$t(window.Vocabulary.silentOperatorWarningTitle),
        message: this.$t(window.Vocabulary.silentOperatorWarningMessage, {
          orderId: this.warning.orderId,
        }),
        penalty: this.$t(window.Vocabulary.silentOperatorWarningPenalty),
        question: this.$t(window.Vocabulary.silentOperatorWarningQuestion),
        preparing: this.$t(window.Vocabulary.silentOperatorWarningPreparing),
        timeLeft: this.$t(window.Vocabulary.silentOperatorWarningTimeLeft, {
          seconds: this.secondsLeft,
        }),
        accept: this.$t(window.Vocabulary.silentOperatorWarningAccept),
        decline: this.$t(window.Vocabulary.silentOperatorWarningDecline),
      };
    },
    serverSecondsLeft() {
      if (
        this.warning.responseSecondsLeft === null
        || typeof this.warning.responseSecondsLeft === "undefined"
      ) {
        if (!this.warning.responseDeadlineAt) {
          return null;
        }

        return Math.max(0, Number(this.warning.responseTimeoutSeconds) || 0);
      }

      return Math.max(0, Number(this.warning.responseSecondsLeft) || 0);
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(value) {
        if (value) {
          this.startTimer();
        } else {
          this.stopTimer();
        }
      },
    },
    warning() {
      if (this.show) {
        this.startTimer();
      }
    },
  },
  beforeDestroy() {
    this.stopTimer();
  },
  methods: {
    startTimer() {
      this.stopTimer();
      this.timeoutEmitted = false;
      this.timerReady = this.serverSecondsLeft !== null;
      this.secondsLeft = this.timerReady ? this.serverSecondsLeft : 0;
      if (!this.timerReady) {
        return;
      }
      if (this.secondsLeft <= 0) {
        this.emitTimeout();
        return;
      }

      this.timer = setInterval(() => {
        this.secondsLeft = Math.max(0, this.secondsLeft - 1);
        if (this.secondsLeft <= 0) {
          this.stopTimer();
          this.emitTimeout();
        }
      }, 1000);
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    emitTimeout() {
      if (this.timeoutEmitted) {
        return;
      }
      this.timeoutEmitted = true;
      this.$emit("timeout");
    },
    accept() {
      this.$emit("accept");
    },
    decline() {
      this.$emit("decline");
    },
  },
};
</script>

<style lang="scss" scoped>
.silent-operator-warning {
  color: $titleColor;
  font-size: 17px;
  line-height: 1.4;
  text-align: center;

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    margin: 0 auto 18px;
    border-radius: 50%;
    background: #fff1ef;
    color: #ef4b45;
    font-family: "TT Norms Bold";
    font-size: 32px;
    line-height: 1;
  }

  &__message {
    margin-bottom: 22px;
    font-family: "TT Norms Bold";
    font-size: 25px;
    line-height: 1.3;
  }

  &__penalty {
    margin-bottom: 22px;
    padding: 14px 18px;
    border: 1px solid #ffd1cd;
    border-radius: 12px;
    background: #fff7f6;
    color: #d83f39;
    font-family: "TT Norms Bold";
  }

  &__question {
    margin-bottom: 18px;
    font-family: "TT Norms Bold";
    font-size: 20px;
    line-height: 1.35;
  }

  &__countdown {
    display: inline-block;
    min-width: 230px;
    padding: 9px 16px;
    border-radius: 20px;
    background: #eef4ff;
    color: #225fcb;
    font-family: "TT Norms Bold";
    font-size: 16px;
  }

  &__actions {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  &__button {
    min-width: 210px;
    height: 48px;
    margin: 0 7px;

    /deep/ .v-btn__content {
      font-family: "TT Norms Bold";
      font-size: 16px;
      text-transform: none;
    }
  }
}

@media (max-width: 680px) {
  .silent-operator-warning {
    &__message {
      font-size: 22px;
    }

    &__actions {
      flex-direction: column-reverse;
      align-items: stretch;
    }

    &__button {
      width: 100%;
      margin: 5px 0;
    }
  }
}
</style>

<style lang="scss">
.silent-operator-warning-dialog {
  .base-modal-card {
    border-radius: 18px;
    overflow: hidden;
  }

  .v-card__title {
    justify-content: center;
    padding: 28px 36px 10px;
    color: $titleColor;
    font-family: "TT Norms Bold";
    font-size: 23px;
    line-height: 1.25;
    text-align: center;

    .spacer {
      display: none;
    }
  }

  .v-card__text {
    padding: 12px 42px 8px;
  }

  .v-card__actions {
    padding: 14px 32px 30px;
  }
}

@media (max-width: 680px) {
  .silent-operator-warning-dialog {
    .v-card__title {
      padding: 24px 22px 8px;
      font-size: 21px;
    }

    .v-card__text {
      padding: 10px 22px 6px;
    }

    .v-card__actions {
      padding: 12px 16px 24px;
    }
  }
}
</style>
